import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import {
  GridCallbackDetails,
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { TripLeg } from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import { SortModel } from '../../../data/models/sort-model.model';

import { ReservationEventType } from '../../../services/webSocketService/updateReservation';
import { TripLegEventType } from '../../../services/webSocketService/updateTripLeg';

import * as triplegSlice from '../../../data/tripleg.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';

import { DataGridSearch } from '../../tables/DataGridPro';
import { MonitorTripsTable } from './MonitorTripsTable';
import MonitorTodaysTripsTable from './MonitorTodaysTripsTable';

const PREFIX = 'MonitorTrips';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  filter: `${PREFIX}-filter`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3), // Adds bottom margin to all child components except the last one
    },
    boxShadow: '0px 0px 10px #dfdfdf',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
    flexGrow: 1,
  },
  [`& .${classes.filter}`]: {
    width: 200,
  },
}));

enum MonitoringTabs {
  todaysTrips = 'todaysTrips',
  exceptions = 'exceptions',
}

const tripLegFilterToEventTypesMap = {
  draft: [
    TripLegEventType.DraftTripLegUpdated,
    TripLegEventType.TripLegDrafted,
  ],
  onhold: [TripLegEventType.OnHoldTripLegUpdated, TripLegEventType.TripLegHeld],
  open: [
    ReservationEventType.ReservationActivated,
    TripLegEventType.OpenTripLegUpdated,
    TripLegEventType.TripLegOpened,
  ],
  offered: [
    TripLegEventType.TripLegOffered,
    TripLegEventType.OfferedTripLegUpdated,
  ],
  accepted: [
    TripLegEventType.TripLegAccepted,
    TripLegEventType.AcceptedTripLegUpdated,
  ],
  confirmed: [TripLegEventType.TripLegConfirmed],
  onsite: [TripLegEventType.TripLegArrived],
  onboard: [TripLegEventType.TripLegBoarded],
  canceled: [TripLegEventType.TripLegCanceled],
  noload: [TripLegEventType.TripLegNotLoaded],
  completed: [TripLegEventType.TripLegCompleted],
  denied: [TripLegEventType.TripLegDenied],
  abandoned: [TripLegEventType.TripLegAbandoned],
  driverintransit: [TripLegEventType.TripLegDriverInTransit],
  canceledlate: [TripLegEventType.TripLegCanceledLate],
};

export default function MonitorTrips() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const dataGridApiRef = useGridApiRef();

  const allTripLegs = useAppSelector(triplegSlice.selectAllTripLegs);
  const todaysTripsWithExceptions = useAppSelector(
    triplegSlice.selectTodaysTripsWithExceptions,
  );
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );

  const allTripLegsLoading = useAppSelector(
    triplegSlice.selectLoadingAllTripLegs,
  );
  const page = useAppSelector(triplegSlice.selectMonitorPage);
  const pageSize = useAppSelector(triplegSlice.selectMonitorPageSize);
  const sortModel = useAppSelector(triplegSlice.selectMonitorPageSortModel);

  const FILTER_ALL = 'ALL';
  const [currentFilter, setCurrentFilter] = useState(FILTER_ALL);
  const [filteredTripLegs, setFilteredTripLegs] = useState<TripLeg[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  let queryParams: any = null;
  let location: any = null;

  if (typeof window !== 'undefined') {
    location = window.location;
    queryParams = new URLSearchParams(location.search);
  }
  const [currentTab, setCurrentTab] = useState(
    queryParams?.get('tab') || MonitoringTabs.todaysTrips,
  );

  const filterOptions = [
    { label: t('triplegInventory.filterAll'), value: FILTER_ALL },
    {
      label: TripLegStatus.Draft.toUpperCase(),
      value: TripLegStatus.Draft,
    },
    {
      label: TripLegStatus.OnHold.toUpperCase(),
      value: TripLegStatus.OnHold,
    },
    {
      label: TripLegStatus.Open.toUpperCase(),
      value: TripLegStatus.Open,
    },
    {
      label: TripLegStatus.Offered.toUpperCase(),
      value: TripLegStatus.Offered,
    },
    {
      label: TripLegStatus.Accepted.toUpperCase(),
      value: TripLegStatus.Accepted,
    },
    {
      label: TripLegStatus.Confirmed.toUpperCase(),
      value: TripLegStatus.Confirmed,
    },
    { label: TripLegStatus.Onsite.toUpperCase(), value: TripLegStatus.Onsite },
    {
      label: TripLegStatus.Onboard.toUpperCase(),
      value: TripLegStatus.Onboard,
    },
    {
      label: TripLegStatus.Canceled.toUpperCase(),
      value: TripLegStatus.Canceled,
    },
    { label: TripLegStatus.NoLoad.toUpperCase(), value: TripLegStatus.NoLoad },
    {
      label: TripLegStatus.Completed.toUpperCase(),
      value: TripLegStatus.Completed,
    },
    { label: TripLegStatus.Denied.toUpperCase(), value: TripLegStatus.Denied },
    {
      label: TripLegStatus.Abandoned.toUpperCase(),
      value: TripLegStatus.Abandoned,
    },
    {
      label: TripLegStatus.InTransit.toUpperCase(),
      value: TripLegStatus.InTransit,
    },
    {
      label: TripLegStatus.CanceledLate.toUpperCase(),
      value: TripLegStatus.CanceledLate,
    },
  ];

  const filterTripLegs = (filter: string) => {
    if (filter === FILTER_ALL) {
      setFilteredTripLegs(allTripLegs.items as unknown as TripLeg[]);
    } else {
      const ucFilter = filter.toUpperCase();
      setFilteredTripLegs(
        allTripLegs.items.filter(
          (tl) => (tl as any).tripLegStatusBC === ucFilter,
        ) as unknown as TripLeg[],
      );
    }
  };

  const fetchTripLegs = useCallback(() => {
    const today = DateTime.now().toISODate();
    dispatch(
      triplegSlice.fetchAllTripLegsAsync({
        page,
        pageSize,
        sortModel: sortModel ?? undefined,
        tripLegStatusBCs:
          currentFilter === FILTER_ALL
            ? undefined
            : [currentFilter.toUpperCase()],
        appointmentStartDate: today,
        appointmentEndDate: today,
        search: searchTerm?.trim() || undefined,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, currentFilter, searchTerm]);

  const fetchTodaysTripsWithExceptions = useCallback(() => {
    const today = DateTime.now().toISODate();
    dispatch(
      triplegSlice.fetchTodaysTripLegsWithExceptionsAsync({
        page,
        pageSize,
        sortModel,
        tripLegStatusBCs:
          currentFilter === FILTER_ALL
            ? undefined
            : [currentFilter.toUpperCase()],
        appointmentStartDate: today,
        appointmentEndDate: today,
        tripLegExceptionBC: [
          'DRIVERLATE',
          'OPEN',
          'NODRIVER',
          'DRIVERNOTONLINE',
        ],
      }),
    );
  }, [dispatch, page, pageSize, sortModel, currentFilter]);

  useEffect(() => {
    if (queryParams) {
      setCurrentTab(queryParams.get('tab') || MonitoringTabs.todaysTrips);
    }
  }, [location]);

  useEffect(() => {
    fetchTripLegs();
    fetchTodaysTripsWithExceptions();
  }, [fetchTripLegs, fetchTodaysTripsWithExceptions]);

  useEffect(() => {
    if (tripLegEventEmitted) {
      if (
        tripLegEventEmitted.detailType === TripLegEventType.TripLegException
      ) {
        fetchTripLegs();
        fetchTodaysTripsWithExceptions();
      } else {
        // If the event is on the current page, we need to refetch
        // to ensure the data is up to date regardless of filter or event
        const index = filteredTripLegs.findIndex(
          (tripLeg) =>
            tripLeg.tripLegReferenceID ===
            tripLegEventEmitted.detail.tripLegReferenceID,
        );
        if (index >= 0) {
          fetchTripLegs();
          fetchTodaysTripsWithExceptions();
        } else if (currentFilter === FILTER_ALL) {
          // If the filter is all, we need to refetch everything regardless
          // to ensure pagination is correct
          fetchTripLegs();
          fetchTodaysTripsWithExceptions();
        } else {
          // If the filter is not all, we need to check what filter is currently selected
          // and if the event type is one of the events that should trigger a refetch
          const tripLegEvents =
            tripLegFilterToEventTypesMap[
              currentFilter as keyof typeof tripLegFilterToEventTypesMap
            ];
          if (
            tripLegEvents.includes(
              tripLegEventEmitted.detailType as TripLegEventType,
            )
          ) {
            fetchTripLegs();
            fetchTodaysTripsWithExceptions();
          }
        }
      }
      dispatch(triplegSlice.setTripLegEventEmitted(null));
    }
  }, [
    tripLegEventEmitted,
    fetchTripLegs,
    fetchTodaysTripsWithExceptions,
    dispatch,
  ]);

  useEffect(() => {
    if (allTripLegs) {
      filterTripLegs(currentFilter);
    }
  }, [allTripLegs]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('passengerCareLevel'));
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
  }, []);

  const handleTripLegFilterChange = (event: any) => {
    const f = event.target.value;
    setCurrentFilter(f);
    filterTripLegs(f);
  };

  const handleSearchChange = (value: string) => {
    const term = value?.trim() ?? '';
    const searchChanged = term !== (searchTerm?.trim() ?? '');
    setSearchTerm(value);
    if (searchChanged) {
      dispatch(triplegSlice.setMonitorPage(1));
    }
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== allTripLegs.currentPage - 1) {
      dispatch(triplegSlice.setMonitorPage(model.page + 1));
    }
    if (model.pageSize !== pageSize) {
      dispatch(triplegSlice.setMonitorPageSize(model.pageSize));
    }
  };

  const sortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    }
    dispatch(triplegSlice.setMonitorPage(1));
    dispatch(triplegSlice.setMonitorPageSortModel(newModel));
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {currentTab === MonitoringTabs.exceptions
            ? t('monitoring.exceptions')
            : t('monitoring.todaysTrips')}
        </Typography>
        {currentTab !== MonitoringTabs.exceptions && (
          <TextField
            select
            value={currentFilter}
            className={classes.filter}
            required={false}
            onChange={handleTripLegFilterChange}
            label={t('triplegInventory.filterStatus')}
            variant="outlined">
            {filterOptions.map((opt) => {
              return (
                <MenuItem value={opt.value} key={opt.value}>
                  {opt.label}
                </MenuItem>
              );
            })}
          </TextField>
        )}
      </Box>
      {currentTab !== MonitoringTabs.exceptions && (
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <DataGridSearch
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder={t('monitoring.searchPlaceholder')}
          />
        </Box>
      )}
      {currentTab !== MonitoringTabs.exceptions ? (
        <MonitorTripsTable
          page={page}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 40, 50, 100]}
          rowCount={allTripLegs.itemCount}
          paginationChange={paginationChange}
          sortModelChange={sortModelChange}
          tripLegs={filteredTripLegs}
          loading={allTripLegsLoading}
          isSelectingTrips={false}
          showReopenAction={false}
          showInProgress={true}
          showAssignAction={false}
          showEditTimesAction={false}
          dataGridApiRef={dataGridApiRef}
        />
      ) : (
        <MonitorTodaysTripsTable
          page={page}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 40, 50, 100]}
          rowCount={todaysTripsWithExceptions?.itemCount}
          paginationChange={paginationChange}
          sortModelChange={sortModelChange}
          tripLegs={todaysTripsWithExceptions.items}
          loading={allTripLegsLoading}
          isSelectingTrips={false}
          showReopenAction={false}
          showInProgress={true}
          showAssignAction={false}
          showEditTimesAction={false}
          dataGridApiRef={dataGridApiRef}
        />
      )}
    </StyledBox>
  );
}
